import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, Download, FileText, Link2, Paperclip, RefreshCw, Trash2, X } from 'lucide-react'
import { Link } from '../router.js'
import { Trail } from '../navigation/Trail.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { Select, useToast } from '../admin/primitives.js'
import { Combobox } from '../admin/pickers.js'
import { Field, TextInput } from '../admin/form.js'
import { guardedClose, useSaveBar } from '../shell/editing.js'
import { useConfirm } from '../shell/confirm.js'
import { setPageTitle } from '../prefs.js'
import {
  chargerFiche, chargerMoi, chargerResponsables, lierMateriel, modifier, retirerFichier, retirerLien, urlFichier,
} from './signalements-api.js'
import {
  Intentions, actionsPermises, apresEnregistrement, brouillonDe, corpsModification, dateCivileParis, dateHeureParis, decrireCible,
  estImage, estOuverte, filtresVersUrl, FILTRES_DEFAUT, libelle, limite, messageErreurFichier, modificateurPoint,
  nombreDeModifications, nomDe, refusFichier, relireSousSaisie, retrouverAction, tailleLisible, texteGardeStatut, tonDe, typesAcceptes, validerSaisie,
  type ActionStatut, type Brouillon, type Changement, type Droits, type ErreurSig, type Fiche, type Fichier,
  type Lien, type Materiel, type MetaSignalements, type Responsable,
} from './signalements-model.js'
import {
  Chargement, EtatErreur, Fait, Pastille, RechercheMateriel, ZoneTexte, deposerFichiers, useMetaSignalements,
} from './SignalementsCommun.js'
import { Activite } from './SignalementActivite.js'
import { ModaleStatut } from './SignalementStatutModale.js'

/**
 * SIGNALEMENTS — LA FICHE (`/technique/signalements/<référence>`, 09/10/2026).
 *
 * ═══ TECHNIQUE EST LA SOURCE DE VÉRITÉ ═══
 * Tout ce que la fiche montre est relu chez Technique par le serveur de
 * GESTION ; rien n'est gardé au-delà de l'écran.
 *
 *  · Les boutons de statut sont `transitions_possibles` ∩ droits GESTION :
 *    l'écran ne calcule aucune transition (`actionsPermises`).
 *  · Les informations s'éditent sur place et la BARRE D'ENREGISTREMENT paraît
 *    dès qu'un champ diffère de la fiche lue. Le PATCH part avec la `version`
 *    lue ; si un collègue a modifié la fiche entre-temps (409
 *    `VERSION_CONFLICT`), une alerte PERSISTANTE le dit, « Actualiser » relit
 *    la fiche, montre ce qui a changé et GARDE la saisie : c'est l'utilisateur
 *    qui réenregistre. Jamais de nouvel essai automatique.
 *  · TOUTE relecture pendant une saisie (après un lien, une pièce jointe, un
 *    commentaire) suit la même conduite (`relireSousSaisie`) : la fiche relue
 *    devient la base du prochain PATCH, donc si elle a changé sous la saisie,
 *    l'alerte le montre — sinon la modification d'un collègue serait écrasée
 *    sans 409. Une fiche FERMÉE entre-temps liste la saisie qui ne peut plus
 *    partir, au lieu de promettre « enregistrez de nouveau ».
 *  · Un changement de statut qui FERME la fiche (résoudre, rejeter, doublon)
 *    ne part pas sur une saisie non enregistrée : la modale « Modifications
 *    non enregistrées » du socle (`guardedClose`) propose d'enregistrer
 *    d'abord ou d'abandonner. Sans elle, la fiche fermée passait en lecture
 *    seule et la saisie disparaissait sans un mot.
 *  · Commentaires, liens et pièces jointes sont des actions atomiques, hors de
 *    la barre ; chaque création porte sa clé d'idempotence (`Intentions`).
 *  · Les liens « panne » existants sont montrés ; aucun sélecteur de panne
 *    n'est proposé : aucune API ne permet d'en choisir une.
 */

type Conflit = null | { etape: 'detecte' } | { etape: 'actualise'; changements: Changement[]; bloquees: Changement[] }

/**
 * Le texte de la barre après un 409. La barre garde son erreur jusqu'au
 * prochain essai (seuls Enregistrer et Annuler l'effacent, socle) : il doit
 * rester vrai APRÈS « Actualiser », après « Compris » qui retire l'alerte, et
 * quand la barre reparaît à la modification suivante. Il ne renvoie donc à
 * rien d'autre qu'à la fiche elle-même.
 */
const ERREUR_BARRE_CONFLIT = 'Non enregistré : quelqu’un d’autre a modifié la fiche entre-temps. Relisez sa version à jour, puis enregistrez de nouveau.'

export function SignalementFichePage({ reference }: { reference: string }) {
  const { etat, recharger } = useMetaSignalements()
  if (etat.phase === 'ok') return <FicheEcran reference={reference} meta={etat.meta} droits={etat.droits} />
  return (
    <main className="lc-page lc-page--management lc-stagger sig-page">
      <Trail leaf={reference} />
      <SmartBack />
      {etat.phase === 'chargement' ? <Chargement libelle="Chargement du signalement" />
        : <EtatErreur erreur={etat.erreur} quoi="le signalement" onReessayer={recharger} />}
    </main>
  )
}

function FicheEcran({ reference, meta, droits }: { reference: string; meta: MetaSignalements; droits: Droits }) {
  const toast = useToast()
  const confirm = useConfirm()
  const [fiche, setFiche] = useState<Fiche | null>(null)
  const [erreur, setErreur] = useState<ErreurSig | null>(null)
  const [brouillon, setBrouillon] = useState<Brouillon | null>(null)
  const [conflit, setConflit] = useState<Conflit>(null)
  const [champs, setChamps] = useState<Record<string, string>>({})
  const [action, setAction] = useState<ActionStatut | null>(null)
  const [relecture, setRelecture] = useState(0)
  const [actualisation, setActualisation] = useState(false)
  const [moi, setMoi] = useState<string | null>(null)
  const [responsables, setResponsables] = useState<Responsable[]>([])
  const [essai, setEssai] = useState(0)
  // Le dernier état lu et la dernière saisie, pour comparer sans dépendre d'un
  // rendu (une relecture rend la main après un `await`, la saisie a pu bouger).
  const ficheRef = useRef<Fiche | null>(null)
  ficheRef.current = fiche
  const brouillonRef = useRef<Brouillon | null>(null)
  brouillonRef.current = brouillon

  useEffect(() => {
    let vivant = true
    setErreur(null)
    void chargerFiche(reference).then((r) => {
      if (!vivant) return
      if (r.ok) { setFiche(r.data); setBrouillon(brouillonDe(r.data)) } else setErreur(r.erreur)
    })
    return () => { vivant = false }
  }, [reference, essai])

  useEffect(() => {
    let vivant = true
    void chargerMoi().then((m) => { if (vivant) setMoi(m?.id ?? null) })
    if (droits.manage && droits.update) void chargerResponsables().then((r) => { if (vivant && r.ok) setResponsables(r.data) })
    return () => { vivant = false }
  }, [droits.manage, droits.update])

  // L'onglet porte la référence, même quand l'adresse est un identifiant numérique.
  useEffect(() => {
    if (!fiche) return
    const t = window.setTimeout(() => setPageTitle(fiche.reference), 0)
    return () => window.clearTimeout(t)
  }, [fiche?.reference]) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Une fiche plus récente : les champs non touchés suivent, la saisie reste.
   * `verifiee` : la fiche vient de la réponse à une transition faite ICI,
   * acceptée par Technique sur la version lue — personne n'a rien changé
   * entre-temps. Sinon (relecture après un lien, un fichier, un commentaire),
   * une fiche qui a changé sous une saisie se MONTRE (`relireSousSaisie`).
   */
  const appliquer = (nouvelle: Fiche, verifiee = false) => {
    const ancienne = ficheRef.current
    const saisie = brouillonRef.current
    setFiche(nouvelle)
    setRelecture((n) => n + 1)
    if (!ancienne || !saisie) { setBrouillon(brouillonDe(nouvelle)); return }
    const r = relireSousSaisie(ancienne, nouvelle, saisie, meta, responsables)
    setBrouillon(r.brouillon)
    if (!verifiee && r.alerter) setConflit({ etape: 'actualise', changements: r.changements, bloquees: r.bloquees })
    // Rouverte ICI : la saisie bloquée redevient enregistrable (la barre reparaît) ; l'alerte « rouvrez-le » n'a plus d'objet.
    else if (verifiee && estOuverte(nouvelle, meta)) setConflit((c) => (c?.etape === 'actualise' && c.bloquees.length > 0 ? null : c))
  }

  const relire = async (): Promise<Fiche | null> => {
    const r = await chargerFiche(reference)
    if (r.ok) return r.data
    toast('error', `${r.erreur.message}${r.erreur.traceId ? ` (réf. ${r.erreur.traceId.slice(0, 8)})` : ''}`)
    return null
  }

  /** Après un lien, une pièce jointe, un commentaire : la fiche (et son activité) se relit. */
  const relireApresAction = async () => {
    const f = await relire()
    if (f) appliquer(f)
    else setRelecture((n) => n + 1)
  }

  /** « Actualiser » : relit, montre ce qui a changé, GARDE la saisie locale. Rend ce qui a changé (null : échec). */
  const actualiser = async (): Promise<Changement[] | null> => {
    const avant = ficheRef.current
    if (!avant || !brouillonRef.current) return null
    setActualisation(true)
    const apres = await relire()
    setActualisation(false)
    const saisie = brouillonRef.current
    if (!apres || !saisie) return null
    const r = relireSousSaisie(avant, apres, saisie, meta, responsables)
    setFiche(apres)
    setBrouillon(r.brouillon)
    setRelecture((n) => n + 1)
    if (conflit || r.alerter) setConflit({ etape: 'actualise', changements: r.changements, bloquees: r.bloquees })
    else toast('ok', r.changements.length === 0 ? 'La fiche était déjà à jour.' : 'Fiche actualisée.')
    return r.changements
  }

  const ouverte = fiche ? estOuverte(fiche, meta) : false
  const editable = !!fiche && droits.update && ouverte
  const responsableEditable = editable && droits.manage
  const base = fiche ? brouillonDe(fiche) : null
  const corps = fiche && base && brouillon && editable ? corpsModification(base, brouillon, fiche.version, responsableEditable) : null
  const locales = brouillon ? validerSaisie(brouillon, meta) : {}

  useSaveBar({
    dirty: corps !== null,
    count: base && brouillon ? nombreDeModifications(base, brouillon, responsableEditable) : 0,
    saveLabel: 'Enregistrer',
    successMessage: 'Signalement mis à jour',
    invalid: corps ? Object.values(locales)[0] ?? null : null,
    onCancel: () => { if (fiche) { setBrouillon(brouillonDe(fiche)); setChamps({}) } },
    onSave: async () => {
      if (!fiche || !corps || !brouillon) return true
      // La saisie qui part. Les champs restent modifiables pendant l'envoi (jusqu'à 10 s) :
      // au succès, ce qui a été tapé entre-temps reste (`apresEnregistrement`), la barre reparaît pour lui.
      const envoye = brouillon
      const r = await modifier(fiche.reference, corps)
      if (r.ok) {
        setFiche(r.data)
        setBrouillon((b) => (b ? apresEnregistrement(envoye, r.data, b) : brouillonDe(r.data)))
        setChamps({})
        setConflit(null)
        setRelecture((n) => n + 1)
        return true
      }
      if (r.erreur.genre === 'version') {
        setConflit({ etape: 'detecte' })
        return ERREUR_BARRE_CONFLIT
      }
      setChamps(r.erreur.champs)
      return `${r.erreur.message}${r.erreur.traceId ? ` (réf. ${r.erreur.traceId.slice(0, 8)})` : ''}`
    },
  })

  if (!fiche || !brouillon) {
    return (
      <main className="lc-page lc-page--management lc-stagger sig-page">
        <Trail leaf={reference} />
        <SmartBack />
        {erreur ? (
          <>
            <EtatErreur erreur={erreur} quoi="le signalement" onReessayer={erreur.genre === 'introuvable' ? undefined : () => setEssai((n) => n + 1)} />
            {erreur.genre === 'introuvable' ? (
              <p className="sig-aide"><Link href="/technique/signalements" className="sig-lien">Revenir à la liste des signalements</Link></p>
            ) : null}
          </>
        ) : <Chargement libelle="Chargement du signalement" />}
      </main>
    )
  }

  const actions = actionsPermises(fiche, meta, droits)
  // La modale suit la fiche COURANTE : relue après un conflit, la MÊME action (même cible, même nature) est
  // retrouvée, ou elle n'est plus possible — jamais une autre action sur la même cible (`retrouverAction`).
  const actionCourante = action ? retrouverAction(actions, action) : null
  /**
   * Un statut qui FERME la fiche rendrait la saisie en cours impossible à
   * enregistrer (formulaire en lecture seule) : la modale du socle demande
   * d'abord d'enregistrer ou d'abandonner. Un statut qui la laisse ouverte
   * garde la saisie (elle suit la fiche relue).
   */
  const ouvrirAction = (a: ActionStatut) => {
    const fermante = !estOuverte({ statut: a.cible, etat: fiche.etat }, meta)
    // On ne quitte pas la page : la modale du socle dit ce qui arrive VRAIMENT (la fiche se ferme).
    guardedClose(corps !== null && fermante, () => setAction(a), texteGardeStatut(a.libelle))
  }
  const poser = <K extends keyof Brouillon>(k: K, v: Brouillon[K]) => {
    setBrouillon((b) => (b ? { ...b, [k]: v } : b))
    setChamps((c) => { const { [k]: _retire, ...reste } = c; return reste })
  }
  const erreurChamp = (k: string): string | null => champs[k] ?? (corps ? locales[k] ?? null : null)

  return (
    <main className="lc-page lc-page--management lc-stagger sig-page">
      <Trail leaf={fiche.reference} />
      <SmartBack />

      <header className="sig-fiche__tete">
        <div className="sig-fiche__identite">
          <span className="sig-ref sig-ref--grande">{fiche.reference}</span>
          <h1 className="lc-title sig-fiche__titre">{fiche.titre}</h1>
          <div className="sig-fiche__pastilles">
            <Pastille ton={tonDe(meta.statuts, fiche.statut)} label={libelle(meta.statuts, fiche.statut)} />
            <Pastille ton={tonDe(meta.priorites, fiche.priorite)} label={`Priorité ${libelle(meta.priorites, fiche.priorite).toLowerCase()}`} />
            <Pastille ton={tonDe(meta.impacts, fiche.impact)} label={`Impact ${libelle(meta.impacts, fiche.impact).toLowerCase()}`} />
          </div>
        </div>
        <div className="sig-fiche__actions">
          {actions.map((a, i) => (
            <button key={a.cible} type="button" className={`lc-btn${i === 0 && !a.sensible ? ' lc-btn--primary' : ''}`}
              onClick={() => ouvrirAction(a)}>
              {a.libelle}
            </button>
          ))}
          {/* Icône seule ; sur téléphone, son libellé paraît : seul sur sa ligne, c'est un vrai bouton, pas un glyphe égaré. */}
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon sig-fiche__actualiser" onClick={() => void actualiser()} disabled={actualisation}
            aria-label="Actualiser la fiche">
            <RefreshCw size={16} aria-hidden="true" />
            <span className="sig-fiche__actualiser-libelle">Actualiser</span>
          </button>
        </div>
      </header>

      {conflit ? (
        <div className="sig-conflit" role="alert">
          <AlertTriangle size={18} aria-hidden="true" className="sig-conflit__icone" />
          <div className="sig-conflit__texte">
            <p className="sig-conflit__titre">Ce signalement a été modifié pendant votre édition.</p>
            {conflit.etape === 'detecte' ? (
              <p>Rien n’a été enregistré, et rien du travail de l’autre personne n’a été écrasé. Actualisez : votre saisie sera conservée.</p>
            ) : (
              <>
                <p>
                  {conflit.changements.length > 0 ? 'Voici ce qui a changé. ' : 'La fiche est à jour. '}
                  {conflit.bloquees.length > 0
                    ? 'Le signalement est maintenant fermé : vos modifications ci-dessous n’ont pas été enregistrées. Pour les appliquer, rouvrez-le, puis enregistrez-les.'
                    : corps !== null
                      ? 'Votre saisie est conservée : relisez-la, puis enregistrez de nouveau si elle tient toujours.'
                      : 'Relisez la fiche avant de recommencer.'}
                </p>
                {conflit.changements.length > 0 ? (
                  <ul className="sig-conflit__liste">
                    {conflit.changements.map((c) => (
                      <li key={c.champ}>
                        <strong>{c.libelle}</strong> : {c.avant} → {c.apres}
                        {c.aussiModifieIci ? <span className="lc-pill lc-pill--warning sig-pastille">vous l’aviez aussi modifié</span> : null}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {conflit.bloquees.length > 0 ? (
                  <>
                    <p className="sig-conflit__titre">Vos modifications non enregistrées</p>
                    <ul className="sig-conflit__liste">
                      {conflit.bloquees.map((c) => <li key={c.champ}><strong>{c.libelle}</strong> : {c.apres}</li>)}
                    </ul>
                  </>
                ) : null}
              </>
            )}
          </div>
          {conflit.etape === 'detecte' ? (
            <button type="button" className="lc-btn lc-btn--sm" onClick={() => void actualiser()} disabled={actualisation}>
              <RefreshCw size={14} aria-hidden="true" /> {actualisation ? 'Lecture…' : 'Actualiser'}
            </button>
          ) : (
            <button type="button" className="lc-btn lc-btn--sm lc-btn--ghost" onClick={() => setConflit(null)}>Compris</button>
          )}
        </div>
      ) : null}

      <div className="sig-fiche__cadre">
      <div className="sig-fiche__grille">
        <div className="sig-fiche__principal">
          <Informations fiche={fiche} meta={meta} brouillon={brouillon} editable={editable}
            responsableEditable={responsableEditable} responsables={responsables} erreur={erreurChamp} poser={poser}
            fermee={!ouverte} peutModifier={droits.update} />
          <Cloture fiche={fiche} />
          <ObjetsLies fiche={fiche} peutLier={droits.update} confirm={confirm} onChange={relireApresAction} />
          <PiecesJointes fiche={fiche} meta={meta} confirm={confirm} peutRetirer={droits.update}
            peutDeposer={droits.update || (droits.create && fiche.reporter?.provider === 'gestion' && !!moi && fiche.reporter.id === moi)}
            onChange={relireApresAction} />
          {/* Technique met à jour `updated_at` pour un commentaire : la fiche se relit, « Mis à jour » suit. */}
          <Activite reference={fiche.reference} meta={meta} relecture={relecture} peutCommenter={droits.update}
            onCommentaire={() => void relireApresAction()} />
        </div>
        <aside className="sig-fiche__cote" aria-label="Repères du signalement">
          <section className="lc-panel sig-section">
            <dl className="sig-section__corps sig-faits">
              <Fait terme="Statut"><Pastille ton={tonDe(meta.statuts, fiche.statut)} label={libelle(meta.statuts, fiche.statut)} /></Fait>
              <Fait terme="Responsable">{fiche.responsable ? nomDe(fiche.responsable) : <span className="sig-manque">Non affecté</span>}</Fait>
              <Fait terme="Signalé par">{nomDe(fiche.reporter)}</Fait>
              <Fait terme="Source">{libelle(meta.sources, fiche.source)}{fiche.source_reference ? ` · ${fiche.source_reference}` : ''}</Fait>
              <Fait terme="Créé le">{dateHeureParis(fiche.created_at)}</Fait>
              <Fait terme="Mis à jour">{dateHeureParis(fiche.updated_at)}</Fait>
              {fiche.closed_at ? <Fait terme="Fermé le">{dateHeureParis(fiche.closed_at)}</Fait> : null}
            </dl>
          </section>
        </aside>
      </div>
      </div>

      {action ? (
        // Sur un 409, la modale RESTE ouverte avec sa saisie (motif, résumé, original) : « Actualiser » y relit la fiche.
        <ModaleStatut action={actionCourante ?? action} possible={actionCourante !== null} fiche={fiche} meta={meta}
          onFermer={() => setAction(null)}
          onConflit={() => setConflit({ etape: 'detecte' })}
          onActualiser={actualiser}
          onFait={(f) => {
            setAction(null)
            appliquer(f, true)
            toast('ok', `${f.reference} : ${libelle(meta.statuts, f.statut)}`)
          }} />
      ) : null}
    </main>
  )
}

/* ═══════════════════════ informations (barre d'enregistrement) ═══════════════════════ */

function Informations(props: {
  fiche: Fiche
  meta: MetaSignalements
  brouillon: Brouillon
  editable: boolean
  responsableEditable: boolean
  responsables: Responsable[]
  erreur: (k: string) => string | null
  poser: <K extends keyof Brouillon>(k: K, v: Brouillon[K]) => void
  fermee: boolean
  peutModifier: boolean
}) {
  const { fiche, meta, brouillon: b } = props
  if (!props.editable) {
    return (
      <section className="lc-panel sig-section" aria-labelledby="sig-infos">
        <div className="sig-section__corps">
          <h2 className="sig-section__titre" id="sig-infos">Informations</h2>
          {props.fermee && props.peutModifier ? (
            <p className="sig-aide">Ce signalement est fermé : rouvrez-le pour modifier ses informations. Commentaires, liens et fichiers restent possibles.</p>
          ) : null}
          <dl className="sig-faits sig-faits--grille">
            <Fait terme="Catégorie">{libelle(meta.categories, fiche.categorie)}</Fait>
            <Fait terme="Emplacement">{fiche.emplacement || <span className="sig-manque">Non précisé</span>}</Fait>
            <Fait terme="Priorité">{libelle(meta.priorites, fiche.priorite)}</Fait>
            <Fait terme="Impact">{libelle(meta.impacts, fiche.impact)}</Fait>
          </dl>
          <h3 className="sig-sous-titre">Description</h3>
          {fiche.description ? <p className="sig-texte">{fiche.description}</p> : <p className="sig-manque">Aucune description.</p>}
        </div>
      </section>
    )
  }
  const impactChoisi = meta.impacts.find((i) => i.code === b.impact)
  const options = (liste: ReadonlyArray<{ code: string; label: string; tone?: string }>) =>
    liste.map((x) => ({ value: x.code, label: x.label, ...(x.tone ? { dot: modificateurPoint(x.tone) } : {}) }))
  return (
    <section className="lc-panel sig-section" aria-labelledby="sig-infos">
      <div className="sig-section__corps">
        <h2 className="sig-section__titre" id="sig-infos">Informations</h2>
        <Field label="Titre" error={props.erreur('titre')}>
          <TextInput value={b.titre} onChange={(v) => props.poser('titre', v)} maxLength={limite(meta, 'titre')} ariaLabel="Titre" />
        </Field>
        <div className="sig-grille">
          <Field label="Catégorie" error={props.erreur('categorie')}>
            <Select value={b.categorie} options={options(meta.categories)} onChange={(v) => props.poser('categorie', v)} ariaLabel="Catégorie" />
          </Field>
          <Field label="Emplacement" error={props.erreur('emplacement')}>
            <TextInput value={b.emplacement} onChange={(v) => props.poser('emplacement', v)} maxLength={limite(meta, 'emplacement')}
              placeholder="Non précisé" ariaLabel="Emplacement" />
          </Field>
          <Field label="Priorité" error={props.erreur('priorite')}>
            <Select value={b.priorite} options={options(meta.priorites)} onChange={(v) => props.poser('priorite', v)} ariaLabel="Priorité" />
          </Field>
          <Field label="Impact" error={props.erreur('impact')} helper={impactChoisi?.description ?? undefined}>
            <Select value={b.impact} options={options(meta.impacts)} onChange={(v) => props.poser('impact', v)} ariaLabel="Impact" />
          </Field>
        </div>
        {props.responsableEditable ? (
          <Field label="Responsable" error={props.erreur('responsable_id')}>
            <Combobox value={b.responsable_id} clearable placeholder="Non affecté" ariaLabel="Responsable"
              onChange={(v) => props.poser('responsable_id', v)}
              items={[
                // Le responsable actuel reste choisissable même s'il n'est plus dans la liste des comptes actifs.
                ...(fiche.responsable && !props.responsables.some((r) => r.id === fiche.responsable!.id)
                  ? [{ value: fiche.responsable.id, label: nomDe(fiche.responsable), hint: 'responsable actuel' }] : []),
                ...props.responsables.map((r) => ({ value: r.id, label: r.display_name, ...(r.can_handle ? {} : { hint: 'sans droit de traitement' }) })),
              ]} />
          </Field>
        ) : null}
        <Field label="Description" error={props.erreur('description')}>
          <ZoneTexte value={b.description} onChange={(v) => props.poser('description', v)} rows={6}
            maxLength={limite(meta, 'description')} ariaLabel="Description" invalide={!!props.erreur('description')} />
        </Field>
      </div>
    </section>
  )
}

/** Ce qui a fermé le signalement : la résolution, le motif du rejet, l'original d'un doublon. */
function Cloture({ fiche }: { fiche: Fiche }) {
  if (!fiche.resolution_summary && !fiche.motif_rejet && !fiche.duplicate_of) return null
  return (
    <section className="lc-panel sig-section sig-cloture" aria-label="Clôture du signalement">
      <div className="sig-section__corps">
        {fiche.resolution_summary ? (<><h2 className="sig-section__titre">Résolution</h2><p className="sig-texte">{fiche.resolution_summary}</p></>) : null}
        {fiche.motif_rejet ? (<><h2 className="sig-section__titre">Motif du rejet</h2><p className="sig-texte">{fiche.motif_rejet}</p></>) : null}
        {fiche.duplicate_of ? (
          <p>Doublon de <Link href={`/technique/signalements/${encodeURIComponent(fiche.duplicate_of.reference)}`} className="sig-lien">{fiche.duplicate_of.reference}</Link> : c’est lui qui porte le suivi.</p>
        ) : null}
      </div>
    </section>
  )
}

/* ═══════════════════════ objets liés ═══════════════════════ */

type Confirmer = ReturnType<typeof useConfirm>

function ObjetsLies(props: { fiche: Fiche; peutLier: boolean; confirm: Confirmer; onChange: () => Promise<void> }) {
  const { fiche } = props
  const toast = useToast()
  const [ouvert, setOuvert] = useState(false)
  const [occupe, setOccupe] = useState(false)
  const intentions = useRef(new Intentions())
  useEffect(() => () => intentions.current.toutOublier(), [])

  const lier = async (m: Materiel) => {
    const intention = `lien:${fiche.reference}:${m.id}`
    setOccupe(true)
    const r = await lierMateriel(fiche.reference, String(m.id), intentions.current.cle(intention))
    intentions.current.apres(intention, r.ok ? null : r.erreur)
    setOccupe(false)
    if (!r.ok) { toast('error', messageAvecTrace(r.erreur)); return }
    toast('ok', `${m.nom} lié au signalement`)
    setOuvert(false)
    await props.onChange()
  }

  const retirer = async (l: Lien) => {
    const ok = await props.confirm({
      title: 'Retirer ce lien ?',
      message: 'Le signalement ne sera plus rattaché à cet objet. L’historique garde la trace du lien et de son retrait.',
      details: [{ label: 'Objet', value: decrireCible(l) }],
      confirmLabel: 'Retirer le lien',
    })
    if (!ok) return
    const r = await retirerLien(fiche.reference, l.id)
    if (!r.ok) { toast('error', messageAvecTrace(r.erreur)); return }
    toast('ok', 'Lien retiré')
    await props.onChange()
  }

  const items = fiche.links.filter((l) => l.domain === 'parc' && l.entity_type === 'item').map((l) => l.entity_id)
  return (
    <section className="lc-panel sig-section" aria-labelledby="sig-liens">
      <div className="sig-section__corps">
        <div className="sig-section__tete">
          <h2 className="sig-section__titre" id="sig-liens">Objets liés <span className="sig-aide">{fiche.links.length || ''}</span></h2>
          {props.peutLier ? (
            <button type="button" className="lc-btn lc-btn--sm" aria-expanded={ouvert} onClick={() => setOuvert((o) => !o)}>
              {ouvert ? <><X size={14} aria-hidden="true" /> Fermer</> : <><Link2 size={14} aria-hidden="true" /> Lier un matériel</>}
            </button>
          ) : null}
        </div>
        {fiche.links.length === 0 ? <p className="sig-aide">Aucun matériel ni aucune panne liés.</p> : (
          <ul className="sig-liens">
            {fiche.links.map((l) => <LigneLien key={l.id} lien={l} peutRetirer={props.peutLier} onRetirer={() => void retirer(l)} />)}
          </ul>
        )}
        {ouvert ? <RechercheMateriel deja={items} occupe={occupe} onChoisir={(m) => void lier(m)} /> : null}
      </div>
    </section>
  )
}

function LigneLien(props: { lien: Lien; peutRetirer: boolean; onRetirer: () => void }) {
  const l = props.lien
  const s = l.snapshot ?? {}
  const txt = (k: string) => (typeof s[k] === 'string' ? (s[k] as string) : null)
  const estItem = l.domain === 'parc' && l.entity_type === 'item'
  const estPanne = l.domain === 'parc' && l.entity_type === 'incident'
  const detail = estItem ? [txt('modele'), txt('salle')].filter(Boolean).join(' · ')
    : estPanne ? [txt('etat_avant') && txt('etat_apres') ? `${txt('etat_avant')} → ${txt('etat_apres')}` : null,
      txt('date_incident') ? dateCivileParis(txt('date_incident')) : null].filter(Boolean).join(' · ')
      : ''
  // Autres signalements sur le même objet : une destination GESTION, jamais l'API interne de Technique.
  const autres = `/technique/signalements?${filtresVersUrl({ ...FILTRES_DEFAUT, state: 'all', objet: { domain: l.domain, entity_type: l.entity_type, entity_id: l.entity_id } }).toString()}`
  return (
    <li className="sig-lienobj">
      <span className="sig-lienobj__type">{estItem ? 'Matériel' : estPanne ? 'Panne' : 'Objet'}</span>
      <span className="sig-lienobj__corps">
        <strong>{decrireCible(l)}</strong>
        {detail ? <span className="sig-aide">{detail}</span> : null}
        {l.target_missing === true ? (
          <span className="lc-pill lc-pill--idle sig-pastille">Retiré du Parc — instantané conservé</span>
        ) : estItem ? (
          <Link href={autres} className="sig-lien">Signalements sur ce matériel</Link>
        ) : null}
      </span>
      {props.peutRetirer ? (
        <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon" aria-label={`Retirer le lien vers ${decrireCible(l)}`} onClick={props.onRetirer}>
          <X size={16} aria-hidden="true" />
        </button>
      ) : null}
    </li>
  )
}

/* ═══════════════════════ pièces jointes ═══════════════════════ */

function PiecesJointes(props: {
  fiche: Fiche
  meta: MetaSignalements
  peutDeposer: boolean
  peutRetirer: boolean
  confirm: Confirmer
  onChange: () => Promise<void>
}) {
  const { fiche, meta } = props
  const toast = useToast()
  const [depot, setDepot] = useState(false)
  const [refus, setRefus] = useState<{ nom: string; raison: string }[]>([])
  const intentions = useRef(new Intentions())
  useEffect(() => () => intentions.current.toutOublier(), [])
  const plein = fiche.files.length >= meta.fichiers.max_par_signalement

  const deposer = async (liste: FileList | null) => {
    if (!liste || liste.length === 0) return
    const ok: File[] = []
    const ko: { nom: string; raison: string }[] = []
    const place = Math.max(0, meta.fichiers.max_par_signalement - fiche.files.length)
    for (const f of Array.from(liste)) {
      const raison = refusFichier(f, meta)
      if (raison) ko.push({ nom: f.name, raison })
      else if (ok.length >= place) ko.push({ nom: f.name, raison: `${meta.fichiers.max_par_signalement} fichiers au plus par signalement.` })
      else ok.push(f)
    }
    setRefus(ko)
    if (ok.length === 0) return
    setDepot(true)
    const bilan = await deposerFichiers(fiche.reference, ok, intentions.current)
    setDepot(false)
    if (bilan.echecs.length > 0) {
      setRefus((r) => [...r, ...bilan.echecs.map((e) => ({ nom: e.nom, raison: messageAvecTrace({ ...e.erreur, message: messageErreurFichier(e.erreur, meta) }) }))])
    }
    if (bilan.deposes.length > 0) {
      toast('ok', bilan.deposes.length > 1 ? `${bilan.deposes.length} fichiers ajoutés` : 'Fichier ajouté')
      await props.onChange()
    }
  }

  const retirer = async (f: Fichier) => {
    const ok = await props.confirm({
      title: 'Retirer ce fichier ?',
      message: 'Le fichier sera effacé chez Technique et ne pourra pas être récupéré. L’historique garde la trace de son dépôt et de son retrait.',
      details: [{ label: 'Fichier', value: f.nom_original }],
      variant: 'critical',
      confirmLabel: 'Retirer le fichier',
    })
    if (!ok) return
    const r = await retirerFichier(fiche.reference, f.id)
    if (!r.ok) { toast('error', messageAvecTrace(r.erreur)); return }
    toast('ok', 'Fichier retiré')
    await props.onChange()
  }

  const images = fiche.files.filter((f) => estImage(f.mime))
  const documents = fiche.files.filter((f) => !estImage(f.mime))
  return (
    <section className="lc-panel sig-section" aria-labelledby="sig-fichiers">
      <div className="sig-section__corps">
        <div className="sig-section__tete">
          <h2 className="sig-section__titre" id="sig-fichiers">Photos et documents <span className="sig-aide">{fiche.files.length || ''}</span></h2>
          {props.peutDeposer ? (
            <label className={`lc-btn lc-btn--sm sig-depot${plein || depot ? ' sig-depot--off' : ''}`} title={plein ? 'Nombre maximal de fichiers atteint.' : undefined}>
              <Paperclip size={14} aria-hidden="true" /> {depot ? 'Dépôt…' : 'Ajouter'}
              <input type="file" multiple className="sig-depot__input" disabled={plein || depot}
                accept={[...meta.fichiers.types.map((t) => t.mime), ...meta.fichiers.types.map((t) => `.${t.extension}`), '.jpeg'].join(',')}
                onChange={(e) => { void deposer(e.target.files); e.target.value = '' }} />
            </label>
          ) : null}
        </div>
        {fiche.files.length === 0 ? <p className="sig-aide">Aucune photo ni aucun document.</p> : null}
        {images.length > 0 ? (
          <ul className="sig-images">
            {images.map((f) => (
              <li key={f.id} className="sig-image">
                <a href={urlFichier(fiche.reference, f.id)} target="_blank" rel="noopener" className="sig-image__lien" aria-label={`Ouvrir ${f.nom_original}`}>
                  <ApercuImage src={urlFichier(fiche.reference, f.id)} nom={f.nom_original} />
                </a>
                <span className="sig-image__legende">
                  <span className="sig-image__texte">
                    <span className="sig-image__nom" title={f.nom_original}>{f.nom_original}</span>
                    <span className="sig-aide">{tailleLisible(f.taille)}</span>
                  </span>
                  {props.peutRetirer ? (
                    <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon" aria-label={`Retirer ${f.nom_original}`} onClick={() => void retirer(f)}>
                      <Trash2 size={15} aria-hidden="true" />
                    </button>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
        {documents.length > 0 ? (
          <ul className="sig-documents">
            {documents.map((f) => (
              <li key={f.id} className="sig-document">
                <FileText size={20} aria-hidden="true" className="sig-document__icone" />
                <span className="sig-document__corps">
                  <span className="sig-image__nom" title={f.nom_original}>{f.nom_original}</span>
                  <span className="sig-aide">{tailleLisible(f.taille)} · déposé {f.created_by ? `par ${nomDe(f.created_by)} ` : ''}le {dateHeureParis(f.created_at)}</span>
                </span>
                {/* `download` sans valeur : le nom vient de l'en-tête du serveur, à l'extension du type servi (jamais le nom d'origine brut). */}
                <a href={urlFichier(fiche.reference, f.id)} download className="lc-btn lc-btn--sm">
                  <Download size={14} aria-hidden="true" /> Télécharger
                </a>
                {props.peutRetirer ? (
                  <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon" aria-label={`Retirer ${f.nom_original}`} onClick={() => void retirer(f)}>
                    <Trash2 size={15} aria-hidden="true" />
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
        {refus.length > 0 ? (
          <ul className="sig-refus" role="alert">
            {refus.map((r, i) => <li key={`${r.nom}-${i}`}><strong>{r.nom}</strong> — {r.raison}</li>)}
          </ul>
        ) : null}
        <p className="sig-aide">{typesAcceptes(meta)}, moins de {tailleLisible(meta.fichiers.taille_max)} chacun, {meta.fichiers.max_par_signalement} fichiers par signalement.</p>
      </div>
    </section>
  )
}

/** Aperçu servi par GESTION (même origine) ; s'il ne vient pas, on le dit au lieu d'une image cassée. */
function ApercuImage({ src, nom }: { src: string; nom: string }) {
  const [rate, setRate] = useState(false)
  if (rate) return <span className="sig-image__absente"><FileText size={22} aria-hidden="true" /> Aperçu indisponible</span>
  return <img src={src} alt={nom} loading="lazy" className="sig-image__img" onError={() => setRate(true)} />
}

function messageAvecTrace(e: ErreurSig): string {
  return `${e.message}${e.traceId ? ` (réf. ${e.traceId.slice(0, 8)})` : ''}`
}
